import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Search, Upload, Grid, List, Image, User, Type, Sparkles, Loader2, X, ScanSearch, BookOpen, Layers, FolderPlus, Star, Trash2, Eye, EyeOff, Tag, Plus, AlertTriangle, Info, FileText, Edit2, Check, Filter, ChevronDown, ChevronUp, Clock } from 'lucide-react'
import clsx from 'clsx'
import { useAuthStore } from '@/stores/authStore'
import { canAccessAssets } from '@/config/access'
import { assetsApi, searchApi, palettesApi, coversApi, analyzerApi, Asset, SimilarAsset, Cover, ImageAnalysis } from '../services/api'
import { UploadAssetModal } from '@/components/assets/UploadAssetModal'
import { GenerateAssetModal } from '@/components/assets/GenerateAssetModal'
import { GenerateHistoryModal } from '@/components/assets/GenerateHistoryModal'

type AssetType = 'all' | 'cover' | 'layout' | 'background' | 'character' | 'typography' | 'effect'

const assetTypeIcons: Record<AssetType, typeof Image> = {
  all: Grid,
  cover: BookOpen,
  layout: Layers,
  background: Image,
  character: User,
  typography: Type,
  effect: Sparkles,
}

const assetTypeLabels: Record<AssetType, string> = {
  all: '전체',
  cover: '표지',
  layout: '통합',
  background: '배경',
  character: '인물',
  typography: '타이포',
  effect: '효과',
}

// 카테고리 계층 구조 정의
const categoryStructure = [
  { type: 'all' as AssetType, level: 0 },
  { type: 'cover' as AssetType, level: 0 },
  { type: 'layout' as AssetType, level: 1, parent: 'cover' },
  { type: 'background' as AssetType, level: 1, parent: 'cover' },
  { type: 'character' as AssetType, level: 1, parent: 'cover' },
  { type: 'typography' as AssetType, level: 1, parent: 'cover' },
  { type: 'effect' as AssetType, level: 1, parent: 'cover' },
]

interface Tag {
  name: string
  name_ko?: string
  count?: number
}

const API_BASE = import.meta.env.VITE_API_URL ||
  (window.location.hostname === 'localhost' ? '' : 'https://aistudio-api.wizice100.workers.dev')

export default function AssetsPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { isAuthenticated, user } = useAuthStore()
  const [assets, setAssets] = useState<Asset[]>([])

  // Embed mode detection
  const embedMode = searchParams.get('embedMode') === 'true'
  const embedType = searchParams.get('type') as AssetType | null
  const selectMode = searchParams.get('selectMode') === 'true'

  // 에셋 라이브러리는 허용된 사용자만 접근 가능 (임베드 선택기 포함 전체 차단)
  const libraryAccessDenied = isAuthenticated && !canAccessAssets(user?.email)

  // 로그인 필수
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true })
    }
  }, [isAuthenticated, navigate])

  // 권한 없는 사용자: 독립 페이지는 홈으로 리다이렉트 (임베드 선택기는 그대로 권한없음 화면 표시)
  useEffect(() => {
    if (libraryAccessDenied && !embedMode) {
      navigate('/', { replace: true })
    }
  }, [libraryAccessDenied, embedMode, navigate])

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedType, setSelectedType] = useState<AssetType>(embedType || 'all')

  // Set type from embed mode on mount
  useEffect(() => {
    if (embedMode && embedType) {
      setSelectedType(embedType)
    }
  }, [embedMode, embedType])

  const [searchQuery, setSearchQuery] = useState('')
  const [activeSearchQuery, setActiveSearchQuery] = useState('')
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'name'>('latest')
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [typeCounts, setTypeCounts] = useState<Record<AssetType, number>>({
    all: 0,
    cover: 0,
    layout: 0,
    background: 0,
    character: 0,
    typography: 0,
    effect: 0,
  })
  const limit = 50

  // Upload and Generate modal state
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [showGenerateModal, setShowGenerateModal] = useState(false)
  const [initialReferenceAsset, setInitialReferenceAsset] = useState<Asset | null>(null)
  const [showHistoryModal, setShowHistoryModal] = useState(false)
  const [reloadTrigger, setReloadTrigger] = useState(0)

  // Similar search modal state
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null)
  const [similarAssets, setSimilarAssets] = useState<SimilarAsset[]>([])
  const [similarLoading, setSimilarLoading] = useState(false)
  const [showSimilarModal, setShowSimilarModal] = useState(false)

  // Similar images advanced filtering
  const [similarityThreshold, setSimilarityThreshold] = useState(70) // 70%
  const [tagFilter, setTagFilter] = useState<'all' | 'has' | 'missing'>('all')
  const [filterTagName, setFilterTagName] = useState('')
  const [similarResultLimit, setSimilarResultLimit] = useState(99) // 기본 99개
  const [searchMode, setSearchMode] = useState<'hybrid' | 'visual' | 'semantic'>('hybrid')
  const [sourceCaption, setSourceCaption] = useState<string | null>(null)

  // Batch edit mode for similar images
  const [editMode, setEditMode] = useState(false)
  const [selectedSimilarIds, setSelectedSimilarIds] = useState<Set<string>>(new Set())
  const [batchTagName, setBatchTagName] = useState('타이포표지')
  const [batchOperation, setBatchOperation] = useState<'add' | 'remove'>('add')
  const [applyingBatchTags, setApplyingBatchTags] = useState(false)

  // Tags state
  const [popularTags, setPopularTags] = useState<Tag[]>([])
  const [selectedTag, setSelectedTag] = useState<string | null>(null)
  const [userTags, setUserTags] = useState<Array<{ name: string; count: number }>>([])
  const [selectedUserTag, setSelectedUserTag] = useState<string | null>(null)

  // Advanced Search State
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false)

  // Project (Cover) state for palette
  const [projects, setProjects] = useState<Cover[]>([])
  const [showPaletteModal, setShowPaletteModal] = useState(false)
  const [selectedAssetForPalette, setSelectedAssetForPalette] = useState<Asset | null>(null)
  const [addingToPalette, setAddingToPalette] = useState(false)

  // Asset management state
  const [viewFilter, setViewFilter] = useState<'all' | 'favorites' | 'hidden' | 'trash'>('all')
  const [showAssetDetailModal, setShowAssetDetailModal] = useState(false)
  const [selectedAssetForDetail, setSelectedAssetForDetail] = useState<Asset | null>(null)
  const [assetAnalysis, setAssetAnalysis] = useState<ImageAnalysis | null>(null)
  const [loadingAnalysis, setLoadingAnalysis] = useState(false)
  const [activeTab, setActiveTab] = useState<'info' | 'generation' | 'analysis'>('info')
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [selectedAssetForDelete, setSelectedAssetForDelete] = useState<Asset | null>(null)
  const [newTagInput, setNewTagInput] = useState('')
  const [processingAction, setProcessingAction] = useState(false)
  const [showDetailJson, setShowDetailJson] = useState<{ type: string; data: any } | null>(null)

  // Title editing state
  const [editingTitle, setEditingTitle] = useState(false)
  const [editedTitle, setEditedTitle] = useState('')
  const [savingTitle, setSavingTitle] = useState(false)

  const fetchAssets = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const sortMap: Record<string, string> = {
        latest: 'created_at',
        popular: 'download_count',
        name: 'name',
      }

      const queryType = selectedType === 'all' ? undefined :
        selectedType === 'cover' ? 'background' :
        selectedType === 'layout' ? undefined : selectedType

      if (selectedType === 'layout') {
        const result = await searchApi.tags({
          tags: ['integrated'],
          type: 'cover',
          limit,
          offset: (page - 1) * limit,
        })

        if (result.success && result.data) {
          setAssets(result.data)
          setTotal(result.meta?.total || 0)
          setTypeCounts(prev => ({ ...prev, layout: result.meta?.total || 0 }))
        } else {
          setError(result.error?.message || '통합 표지를 불러오는데 실패했습니다.')
        }
      }
      else if (activeSearchQuery || selectedTag) {
        const searchTerm = (activeSearchQuery || selectedTag)!
        // Use keyword-based search
        const result = await assetsApi.search(searchTerm, {
          limit,
          offset: (page - 1) * limit,
        })

        if (result.success && result.data) {
          setAssets(result.data)
          setTotal(result.meta?.total || 0)

          // Show extracted keywords for debugging
          if (result.meta?.extractedKeywords) {
            console.log(`🔍 Extracted keywords:`, result.meta.extractedKeywords)
            console.log(`📊 Match count: ${result.meta.matchCount}`)
          }
        } else {
          setError(result.error?.message || '검색에 실패했습니다.')
        }
      } else {
        const result = await assetsApi.list({
          type: queryType,
          public: viewFilter === 'all' ? true : undefined,
          favorite: viewFilter === 'favorites' ? true : undefined,
          hidden: viewFilter === 'hidden' ? true : undefined,
          trash: viewFilter === 'trash' ? true : undefined,
          userTag: selectedUserTag || undefined,
          limit,
          offset: (page - 1) * limit,
          sort: sortMap[sortBy],
          order: sortBy === 'name' ? 'ASC' : 'DESC',
        })

        if (result.success && result.data) {
          setAssets(result.data)
          setTotal(result.meta?.total || 0)

          if (selectedType === 'all') {
            setTypeCounts(prev => ({ ...prev, all: result.meta?.total || 0 }))
          } else {
            setTypeCounts(prev => ({ ...prev, [selectedType]: result.meta?.total || 0 }))
          }
        } else {
          setError(result.error?.message || '에셋을 불러오는데 실패했습니다.')
        }
      }
    } catch (err) {
      setError('네트워크 오류가 발생했습니다.')
    } finally {
      setLoading(false)
    }
  }, [selectedType, sortBy, page, selectedTag, activeSearchQuery, viewFilter, selectedUserTag, reloadTrigger])

  // Fetch all type counts and popular tags on initial load
  useEffect(() => {
    const fetchTypeCounts = async () => {
      const types: AssetType[] = ['all', 'cover', 'layout', 'background', 'character', 'typography', 'effect']
      for (const type of types) {
        if (type === 'layout') {
          const result = await searchApi.tags({
            tags: ['integrated'],
            type: 'cover',
            limit: 1,
          })
          if (result.success && result.meta) {
            setTypeCounts(prev => ({ ...prev, layout: result.meta?.total || 0 }))
          }
        } else {
          const queryType = type === 'all' ? undefined :
            type === 'cover' ? 'background' : type
          const result = await assetsApi.list({
            type: queryType,
            public: true,
            limit: 1,
          })
          if (result.success && result.meta) {
            setTypeCounts(prev => ({ ...prev, [type]: result.meta?.total || 0 }))
          }
        }
      }
    }

    const fetchPopularTags = async () => {
      const result = await searchApi.popularTags(undefined, 20)
      if (result.success && result.data) {
        setPopularTags(result.data.map(t => ({
          name: t.name,
          name_ko: t.name_ko,
          count: t.usage_count
        })))
      }
    }

    const fetchUserTags = async () => {
      if (!isAuthenticated) return
      const result = await assetsApi.getUserTags()
      if (result.success && result.data) {
        setUserTags(result.data)
      }
    }

    fetchTypeCounts()
    fetchPopularTags()
    fetchUserTags()
  }, [isAuthenticated])

  useEffect(() => {
    fetchAssets()
  }, [fetchAssets])

  useEffect(() => {
    setPage(1)
  }, [selectedType, sortBy, selectedTag, activeSearchQuery, selectedUserTag])

  const handleSearch = () => {
    setSelectedTag(null)
    setSelectedUserTag(null)
    setActiveSearchQuery(searchQuery.trim())
  }

  const handleClearSearch = () => {
    setSearchQuery('')
    setActiveSearchQuery('')
  }

  const handleTagClick = (tagName: string) => {
    setActiveSearchQuery('')
    setSearchQuery('')
    setSelectedUserTag(null)
    if (selectedTag === tagName) {
      setSelectedTag(null)
    } else {
      setSelectedTag(tagName)
    }
  }

  const handleUserTagClick = (tagName: string) => {
    setActiveSearchQuery('')
    setSearchQuery('')
    setSelectedTag(null)
    if (selectedUserTag === tagName) {
      setSelectedUserTag(null)
    } else {
      setSelectedUserTag(tagName)
    }
  }

  const handleUseAsset = (asset: Asset) => {
    navigate(`/analyzer?assetId=${asset.id}`)
  }

  const totalPages = Math.ceil(total / limit)

  const getImageUrl = (asset: Asset) => {
    if (asset.thumbnailUrl) {
      return `${API_BASE}${asset.thumbnailUrl}`
    }
    return null
  }

  const findSimilarImages = async (asset: Asset, mode?: 'hybrid' | 'visual' | 'semantic') => {
    setSelectedAsset(asset)
    setShowSimilarModal(true)
    setSimilarLoading(true)
    setSimilarAssets([])
    setSourceCaption(null)

    const effectiveMode = mode || searchMode
    try {
      const result = await searchApi.similar(asset.id, similarResultLimit, effectiveMode)
      if (result.success && result.data) {
        setSimilarAssets(result.data)
        setSourceCaption((result as any).meta?.sourceCaption || null)
      }
    } catch (err) {
      console.error('Similar search failed:', err)
    } finally {
      setSimilarLoading(false)
    }
  }

  const closeSimilarModal = () => {
    setShowSimilarModal(false)
    setSelectedAsset(null)
    setSimilarAssets([])
    setEditMode(false)
    setSelectedSimilarIds(new Set())
    setSimilarityThreshold(70)
    setTagFilter('all')
    setFilterTagName('')
    setSimilarResultLimit(20)
    setSearchMode('hybrid')
    setSourceCaption(null)
  }

  // Filter similar assets by threshold and tag
  const getFilteredSimilarAssets = useCallback(() => {
    let filtered = similarAssets.filter(asset => (asset.similarity * 100) >= similarityThreshold)

    if (tagFilter !== 'all' && filterTagName) {
      filtered = filtered.filter(asset => {
        // Check if asset has the specified tag
        const hasTag = asset.tags?.some(tag => tag.name === filterTagName || tag.name_ko === filterTagName) || false
        return tagFilter === 'has' ? hasTag : !hasTag
      })
    }

    return filtered
  }, [similarAssets, similarityThreshold, tagFilter, filterTagName])

  // Toggle selection for batch edit
  const toggleSelection = (assetId: string) => {
    const newSelection = new Set(selectedSimilarIds)
    if (newSelection.has(assetId)) {
      newSelection.delete(assetId)
    } else {
      newSelection.add(assetId)
    }
    setSelectedSimilarIds(newSelection)
  }

  // Select all filtered assets
  const selectAll = () => {
    const filtered = getFilteredSimilarAssets()
    setSelectedSimilarIds(new Set(filtered.map(a => a.id)))
  }

  // Deselect all
  const deselectAll = () => {
    setSelectedSimilarIds(new Set())
  }

  // Apply batch tag operation
  const applyBatchTags = async () => {
    if (selectedSimilarIds.size === 0 || !batchTagName.trim()) return

    setApplyingBatchTags(true)
    try {
      const assetIds = Array.from(selectedSimilarIds)

      // Call API to add or remove system tags
      for (const assetId of assetIds) {
        if (batchOperation === 'add') {
          await assetsApi.addSystemTag(assetId, batchTagName.trim())
        } else {
          await assetsApi.removeSystemTag(assetId, batchTagName.trim())
        }
      }

      // Reset selection
      setSelectedSimilarIds(new Set())
      setEditMode(false)

      // Show success message
      alert(`${assetIds.length}개 asset에 태그 "${batchTagName}" ${batchOperation === 'add' ? '추가' : '삭제'} 완료`)

      // Refresh similar assets to reflect changes
      if (selectedAsset) {
        const result = await searchApi.similar(selectedAsset.id, similarResultLimit, searchMode)
        if (result.success && result.data) {
          setSimilarAssets(result.data)
          setSourceCaption((result as any).meta?.sourceCaption || null)
        }
      }
    } catch (error) {
      console.error('Batch tag operation failed:', error)
      alert('태그 작업 중 오류가 발생했습니다.')
    } finally {
      setApplyingBatchTags(false)
    }
  }

  const fetchProjects = async () => {
    try {
      const result = await coversApi.list({ limit: 100 })
      if (result.success && result.data) {
        setProjects(result.data)
      }
    } catch (err) {
      console.error('Failed to fetch projects:', err)
    }
  }

  useEffect(() => {
    fetchProjects()
  }, [])

  const openPaletteModal = (asset: Asset) => {
    setSelectedAssetForPalette(asset)
    setShowPaletteModal(true)
  }

  const closePaletteModal = () => {
    setShowPaletteModal(false)
    setSelectedAssetForPalette(null)
  }

  const addToProjectPalette = async (coverId: string, projectTitle: string) => {
    if (!selectedAssetForPalette) return

    setAddingToPalette(true)
    try {
      const paletteResult = await coversApi.getPalette(coverId)
      if (!paletteResult.success || !paletteResult.data) {
        alert('프로젝트의 팔레트를 찾을 수 없습니다.')
        return
      }

      const result = await palettesApi.addAssets(paletteResult.data.id, [selectedAssetForPalette.id])
      if (result.success) {
        alert(`"${selectedAssetForPalette.name}"이(가) "${projectTitle}" 팔레트에 추가되었습니다.`)
        closePaletteModal()

        if (embedMode && window.parent) {
          window.parent.postMessage({ type: 'PALETTE_UPDATED' }, window.location.origin)
        }
      } else {
        alert(`팔레트에 추가하는데 실패했습니다: ${result.error?.message}`)
      }
    } catch (err) {
      alert('네트워크 오류가 발생했습니다.')
    } finally {
      setAddingToPalette(false)
    }
  }

  const toggleFavorite = async (asset: Asset, e: React.MouseEvent) => {
    e.stopPropagation()
    setProcessingAction(true)
    try {
      if (asset.isFavorited) {
        const result = await assetsApi.removeFavorite(asset.id)
        if (result.success) {
          setAssets(prev => prev.map(a => a.id === asset.id ? { ...a, isFavorited: false } : a))
        } else {
          alert(result.error?.message || '즐겨찾기 제거에 실패했습니다.')
        }
      } else {
        const result = await assetsApi.addFavorite(asset.id)
        if (result.success) {
          setAssets(prev => prev.map(a => a.id === asset.id ? { ...a, isFavorited: true } : a))
        } else {
          alert(result.error?.message || '즐겨찾기 추가에 실패했습니다.')
        }
      }
    } catch (err) {
      alert('네트워크 오류가 발생했습니다.')
    } finally {
      setProcessingAction(false)
    }
  }

  const openDeleteModal = (asset: Asset, e: React.MouseEvent) => {
    e.stopPropagation()
    setSelectedAssetForDelete(asset)
    setShowDeleteModal(true)
  }

  const closeDeleteModal = () => {
    setShowDeleteModal(false)
    setSelectedAssetForDelete(null)
  }

  const confirmDelete = async () => {
    if (!selectedAssetForDelete) return

    setProcessingAction(true)
    try {
      const result = await assetsApi.delete(selectedAssetForDelete.id)
      if (result.success) {
        await fetchAssets()
        closeDeleteModal()
        alert('Asset이 삭제되었습니다. 30일 이내에 복구할 수 있습니다.')
      } else {
        alert(result.error?.message || '삭제에 실패했습니다.')
      }
    } catch (err) {
      alert('네트워크 오류가 발생했습니다.')
    } finally {
      setProcessingAction(false)
    }
  }

  const hideAsset = async (asset: Asset, e: React.MouseEvent) => {
    e.stopPropagation()
    setProcessingAction(true)
    try {
      const result = await assetsApi.hideAsset(asset.id)
      if (result.success) {
        await fetchAssets()
        alert('Asset이 숨겨졌습니다.')
      } else {
        alert(result.error?.message || '숨기기에 실패했습니다.')
      }
    } catch (err) {
      alert('네트워크 오류가 발생했습니다.')
    } finally {
      setProcessingAction(false)
    }
  }

  const unhideAsset = async (asset: Asset, e: React.MouseEvent) => {
    e.stopPropagation()
    setProcessingAction(true)
    try {
      const result = await assetsApi.unhideAsset(asset.id)
      if (result.success) {
        await fetchAssets()
        alert('Asset 숨김이 해제되었습니다.')
      } else {
        alert(result.error?.message || '숨김 해제에 실패했습니다.')
      }
    } catch (err) {
      alert('네트워크 오류가 발생했습니다.')
    } finally {
      setProcessingAction(false)
    }
  }

  const restoreAsset = async (asset: Asset, e: React.MouseEvent) => {
    e.stopPropagation()
    setProcessingAction(true)
    try {
      const result = await assetsApi.restoreAsset(asset.id)
      if (result.success) {
        await fetchAssets()
        alert('Asset이 복구되었습니다.')
      } else {
        alert(result.error?.message || '복구에 실패했습니다.')
      }
    } catch (err) {
      alert('네트워크 오류가 발생했습니다.')
    } finally {
      setProcessingAction(false)
    }
  }

  const openAssetDetail = async (asset: Asset) => {
    // If in select mode, send message to parent window
    if (selectMode && window.parent !== window) {
      window.parent.postMessage({
        type: 'ASSET_SELECTED',
        asset: asset
      }, window.location.origin)
      return
    }

    setShowAssetDetailModal(true)
    setActiveTab('info')
    setAssetAnalysis(null)
    setEditingTitle(false)
    setEditedTitle(asset.name)

    try {
      const result = await assetsApi.get(asset.id)
      if (result.success && result.data) {
        setSelectedAssetForDetail(result.data)
        setEditedTitle(result.data.name)

        setLoadingAnalysis(true)
        let foundAnalysis = false

        if (result.data.analysisId) {
          const analysisResult = await analyzerApi.getAnalysis(result.data.analysisId)
          if (analysisResult.success && analysisResult.data) {
            setAssetAnalysis(analysisResult.data)
            foundAnalysis = true
          }
        }

        if (!foundAnalysis) {
          const analysesResult = await analyzerApi.listAnalyses({
            assetId: asset.id,
            limit: 1
          })
          if (analysesResult.success && analysesResult.data && analysesResult.data.length > 0) {
            setAssetAnalysis(analysesResult.data[0])
          }
        }

        setLoadingAnalysis(false)
      } else {
        setSelectedAssetForDetail(asset)
      }
    } catch (err) {
      setSelectedAssetForDetail(asset)
      setLoadingAnalysis(false)
    }
  }

  const closeAssetDetail = () => {
    setShowAssetDetailModal(false)
    setSelectedAssetForDetail(null)
    setAssetAnalysis(null)
    setNewTagInput('')
    setEditingTitle(false)
  }

  const handleSaveTitle = async () => {
    if (!selectedAssetForDetail || !editedTitle.trim() || editedTitle === selectedAssetForDetail.name) {
      setEditingTitle(false)
      return
    }

    setSavingTitle(true)
    try {
      const result = await assetsApi.update(selectedAssetForDetail.id, {
        name: editedTitle.trim()
      })

      if (result.success && result.data) {
        setSelectedAssetForDetail(result.data)
        setAssets(prev => prev.map(a => a.id === selectedAssetForDetail.id ? { ...a, name: editedTitle.trim() } : a))
        setEditingTitle(false)
        alert('제목이 변경되었습니다.')
      } else {
        alert(result.error?.message || '제목 변경에 실패했습니다.')
      }
    } catch (err) {
      alert('네트워크 오류가 발생했습니다.')
    } finally {
      setSavingTitle(false)
    }
  }

  const handleCancelEditTitle = () => {
    setEditingTitle(false)
    if (selectedAssetForDetail) {
      setEditedTitle(selectedAssetForDetail.name)
    }
  }

  const addUserTag = async () => {
    if (!selectedAssetForDetail || !newTagInput.trim()) return

    setProcessingAction(true)
    try {
      const result = await assetsApi.addUserTag(selectedAssetForDetail.id, newTagInput.trim())
      if (result.success) {
        setSelectedAssetForDetail(prev => prev ? {
          ...prev,
          userTags: [...(prev.userTags || []), newTagInput.trim()]
        } : null)
        setAssets(prev => prev.map(a => a.id === selectedAssetForDetail.id ? {
          ...a,
          userTags: [...(a.userTags || []), newTagInput.trim()]
        } : a))
        setNewTagInput('')

        const userTagsResult = await assetsApi.getUserTags()
        if (userTagsResult.success && userTagsResult.data) {
          setUserTags(userTagsResult.data)
        }
      } else {
        alert(result.error?.message || '태그 추가에 실패했습니다.')
      }
    } catch (err) {
      alert('네트워크 오류가 발생했습니다.')
    } finally {
      setProcessingAction(false)
    }
  }

  const removeUserTag = async (tagName: string) => {
    if (!selectedAssetForDetail) return

    setProcessingAction(true)
    try {
      const result = await assetsApi.removeUserTag(selectedAssetForDetail.id, tagName)
      if (result.success) {
        setSelectedAssetForDetail(prev => prev ? {
          ...prev,
          userTags: (prev.userTags || []).filter(t => t !== tagName)
        } : null)
        setAssets(prev => prev.map(a => a.id === selectedAssetForDetail.id ? {
          ...a,
          userTags: (a.userTags || []).filter(t => t !== tagName)
        } : a))

        const userTagsResult = await assetsApi.getUserTags()
        if (userTagsResult.success && userTagsResult.data) {
          setUserTags(userTagsResult.data)
        }
      } else {
        alert(result.error?.message || '태그 제거에 실패했습니다.')
      }
    } catch (err) {
      alert('네트워크 오류가 발생했습니다.')
    } finally {
      setProcessingAction(false)
    }
  }

  // 권한 없는 사용자: 라이브러리 페이지 렌더링 차단 (리다이렉트 전 깜빡임 방지)
  if (libraryAccessDenied) {
    return (
      <div className="min-h-[calc(100vh-56px)] flex items-center justify-center">
        <div className="text-center text-text-secondary">
          <p className="text-lg mb-2">접근 권한이 없습니다</p>
          <p className="text-sm">에셋 라이브러리는 관리자만 이용할 수 있습니다.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-[calc(100vh-56px)] flex">
      {/* Sidebar */}
      {!embedMode && (
      <aside className="hidden lg:block w-64 border-r border-border-subtle p-4 space-y-6 overflow-y-auto">
        {/* View Filter */}
        <div>
          <h3 className="text-sm font-semibold text-text-secondary mb-3">보기</h3>
          <div className="space-y-1">
            <button
              onClick={() => setViewFilter('all')}
              className={clsx(
                'w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors',
                viewFilter === 'all'
                  ? 'bg-accent-gold/10 text-accent-gold'
                  : 'text-text-secondary hover:bg-bg-elevated hover:text-text-primary'
              )}
            >
              <Grid className="w-4 h-4" />
              전체
            </button>
            <button
              onClick={() => setViewFilter('favorites')}
              className={clsx(
                'w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors',
                viewFilter === 'favorites'
                  ? 'bg-accent-gold/10 text-accent-gold'
                  : 'text-text-secondary hover:bg-bg-elevated hover:text-text-primary'
              )}
            >
              <Star className="w-4 h-4" />
              즐겨찾기
            </button>
            <button
              onClick={() => setViewFilter('hidden')}
              className={clsx(
                'w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors',
                viewFilter === 'hidden'
                  ? 'bg-accent-gold/10 text-accent-gold'
                  : 'text-text-secondary hover:bg-bg-elevated hover:text-text-primary'
              )}
            >
              <EyeOff className="w-4 h-4" />
              숨김
            </button>
            <button
              onClick={() => setViewFilter('trash')}
              className={clsx(
                'w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors',
                viewFilter === 'trash'
                  ? 'bg-accent-gold/10 text-accent-gold'
                  : 'text-text-secondary hover:bg-bg-elevated hover:text-text-primary'
              )}
            >
              <Trash2 className="w-4 h-4" />
              휴지통
            </button>
          </div>
        </div>

        {/* Category Filter */}
        <div>
          <h3 className="text-sm font-semibold text-text-secondary mb-3">카테고리</h3>
          <div className="space-y-1">
            {categoryStructure.map(({ type, level }) => {
              const Icon = assetTypeIcons[type]
              const isSelected = selectedType === type
              const isCoverChild = level === 1

              return (
                <button
                  key={type}
                  onClick={() => setSelectedType(type)}
                  className={clsx(
                    'w-full flex items-center justify-between py-2 rounded-lg text-sm transition-colors',
                    level === 0 ? 'px-3' : 'px-3 pl-8',
                    isSelected
                      ? 'bg-accent-gold/10 text-accent-gold'
                      : 'text-text-secondary hover:bg-bg-elevated hover:text-text-primary',
                    isCoverChild && 'text-xs'
                  )}
                >
                  <span className="flex items-center gap-2">
                    <Icon className={clsx('w-4 h-4', isCoverChild && 'w-3 h-3')} />
                    {assetTypeLabels[type]}
                  </span>
                  <span className="text-xs">({typeCounts[type].toLocaleString()})</span>
                </button>
              )
            })}
          </div>
        </div>

        {/* Tags Filter */}
        <div>
          <h3 className="text-sm font-semibold text-text-secondary mb-3">인기 태그</h3>
          <div className="space-y-1 max-h-64 overflow-y-auto">
            {popularTags.map((tag) => (
              <button
                key={tag.name}
                onClick={() => handleTagClick(tag.name)}
                className={clsx(
                  'w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors',
                  selectedTag === tag.name
                    ? 'bg-accent-gold/10 text-accent-gold'
                    : 'text-text-secondary hover:bg-bg-elevated hover:text-text-primary'
                )}
              >
                <span className="truncate">#{tag.name_ko || tag.name}</span>
                <span className="text-xs ml-2 flex-shrink-0">({tag.count || 0})</span>
              </button>
            ))}
          </div>
        </div>

        {/* User Tags */}
        {userTags.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-text-secondary mb-3">내 태그</h3>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {userTags.map((tag) => (
                <button
                  key={tag.name}
                  onClick={() => handleUserTagClick(tag.name)}
                  className={clsx(
                    'w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors',
                    selectedUserTag === tag.name
                      ? 'bg-accent-gold/10 text-accent-gold'
                      : 'text-text-secondary hover:bg-bg-elevated hover:text-text-primary'
                  )}
                >
                  <span className="flex items-center gap-1 truncate">
                    <Tag className="w-3 h-3 flex-shrink-0" />
                    <span className="truncate">{tag.name}</span>
                  </span>
                  <span className="text-xs ml-2 flex-shrink-0">({tag.count})</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </aside>
      )}

      {/* Main Content */}
      <main className="flex-1 p-4 lg:p-6 overflow-y-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold">Asset 라이브러리</h1>
            <p className="text-text-secondary text-sm mt-1">
              {activeSearchQuery ? (
                <span className="flex items-center gap-2">
                  <span>"{activeSearchQuery}" 검색 결과: {total.toLocaleString()}개</span>
                  <button
                    onClick={handleClearSearch}
                    className="text-accent-gold hover:text-accent-gold/80 text-xs"
                  >
                    (초기화)
                  </button>
                </span>
              ) : selectedTag ? (
                <span className="flex items-center gap-2">
                  <span>#{selectedTag} 태그: {total.toLocaleString()}개</span>
                  <button
                    onClick={() => setSelectedTag(null)}
                    className="text-accent-gold hover:text-accent-gold/80 text-xs"
                  >
                    (초기화)
                  </button>
                </span>
              ) : selectedUserTag ? (
                <span className="flex items-center gap-2">
                  <Tag className="w-3 h-3 inline" />
                  <span>{selectedUserTag} 태그: {total.toLocaleString()}개</span>
                  <button
                    onClick={() => setSelectedUserTag(null)}
                    className="text-accent-gold hover:text-accent-gold/80 text-xs"
                  >
                    (초기화)
                  </button>
                </span>
              ) : (
                `총 ${total.toLocaleString()}개의 에셋`
              )}
            </p>
          </div>

          {!embedMode && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowUploadModal(true)}
              className="btn btn-secondary"
            >
              <Upload className="w-4 h-4 mr-2" />
              업로드
            </button>
            <div className="flex gap-2">
              <button
                onClick={() => setShowGenerateModal(true)}
                className="btn btn-primary"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                AI 생성
              </button>
              <button
                onClick={() => setShowHistoryModal(true)}
                className="btn btn-secondary p-2"
                title="AI 생성 이력"
              >
                <Clock className="w-4 h-4" />
              </button>
            </div>
          </div>
          )}
        </div>

        {/* Enhanced Search Bar */}
        <div className="mb-6 space-y-4">
          {/* Main Search */}
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-tertiary" />
              <input
                type="text"
                placeholder="제목, 작가, 키워드로 검색... (Enter)"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="input pl-10 pr-10"
              />
              {(searchQuery || activeSearchQuery) && (
                <button
                  onClick={handleClearSearch}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-bg-elevated rounded"
                >
                  <X className="w-4 h-4 text-text-tertiary" />
                </button>
              )}
            </div>
            <button
              onClick={handleSearch}
              className="btn btn-primary px-6"
            >
              <Search className="w-4 h-4 mr-2" />
              검색
            </button>
            <button
              onClick={() => setShowAdvancedSearch(!showAdvancedSearch)}
              className={clsx(
                'btn btn-secondary px-4',
                showAdvancedSearch && 'bg-accent-gold/10 text-accent-gold'
              )}
              title="고급 검색"
            >
              <Filter className="w-4 h-4 mr-2" />
              필터
              {showAdvancedSearch ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />}
            </button>
          </div>

          {/* Advanced Search Panel */}
          {showAdvancedSearch && (
            <div className="bg-bg-elevated rounded-lg p-4 border border-border-default space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Type Filter */}
                <div>
                  <label className="text-xs font-medium text-text-tertiary uppercase block mb-2">타입</label>
                  <div className="flex flex-wrap gap-2">
                    {(['all', 'background', 'character', 'typography', 'effect'] as AssetType[]).map((type) => (
                      <button
                        key={type}
                        onClick={() => setSelectedType(type)}
                        className={clsx(
                          'px-3 py-1.5 rounded-full text-xs font-medium transition-colors',
                          selectedType === type
                            ? 'bg-accent-gold text-white'
                            : 'bg-neutral-800 text-text-secondary hover:bg-neutral-700'
                        )}
                      >
                        {assetTypeLabels[type]}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Sort Filter */}
                <div>
                  <label className="text-xs font-medium text-text-tertiary uppercase block mb-2">정렬</label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
                    className="input w-full"
                  >
                    <option value="latest">최신순</option>
                    <option value="popular">인기순</option>
                    <option value="name">이름순</option>
                  </select>
                </div>
              </div>

              {/* Quick Tag Selection */}
              <div>
                <label className="text-xs font-medium text-text-tertiary uppercase block mb-2">빠른 태그 선택</label>
                <div className="flex flex-wrap gap-2">
                  {popularTags.slice(0, 10).map((tag) => (
                    <button
                      key={tag.name}
                      onClick={() => handleTagClick(tag.name)}
                      className={clsx(
                        'px-3 py-1.5 rounded-full text-xs font-medium transition-colors',
                        selectedTag === tag.name
                          ? 'bg-accent-gold text-white'
                          : 'bg-neutral-800 text-text-secondary hover:bg-neutral-700'
                      )}
                    >
                      #{tag.name_ko || tag.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* View Mode & Sort (Compact) */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm text-text-tertiary">보기:</span>
              <div className="flex border border-border-default rounded-lg overflow-hidden">
                <button
                  onClick={() => setViewMode('grid')}
                  className={clsx(
                    'p-2 transition-colors',
                    viewMode === 'grid' ? 'bg-bg-elevated text-text-primary' : 'text-text-tertiary hover:text-text-secondary'
                  )}
                >
                  <Grid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={clsx(
                    'p-2 transition-colors',
                    viewMode === 'list' ? 'bg-bg-elevated text-text-primary' : 'text-text-tertiary hover:text-text-secondary'
                  )}
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Active Filters Summary */}
            <div className="flex flex-wrap items-center gap-2">
              {selectedType !== 'all' && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-accent-gold/10 text-accent-gold rounded text-xs">
                  {assetTypeLabels[selectedType]}
                  <button onClick={() => setSelectedType('all')} className="hover:bg-accent-gold/20 rounded-full p-0.5">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
              {viewFilter !== 'all' && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-accent-primary/10 text-accent-primary rounded text-xs">
                  {viewFilter === 'favorites' && '즐겨찾기'}
                  {viewFilter === 'hidden' && '숨김'}
                  {viewFilter === 'trash' && '휴지통'}
                  <button onClick={() => setViewFilter('all')} className="hover:bg-accent-primary/20 rounded-full p-0.5">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-accent-gold" />
            <span className="ml-3 text-text-secondary">에셋을 불러오는 중...</span>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="text-center py-20">
            <p className="text-red-500 mb-4">{error}</p>
            <button onClick={fetchAssets} className="btn btn-primary">
              다시 시도
            </button>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && assets.length === 0 && (
          <div className="text-center py-20">
            <Image className="w-16 h-16 mx-auto text-text-tertiary mb-4" />
            <p className="text-text-secondary">에셋이 없습니다.</p>
          </div>
        )}

        {/* Assets Grid */}
        {!loading && !error && assets.length > 0 && (
          <div className={clsx(
            'grid gap-4',
            viewMode === 'grid'
              ? 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5'
              : 'grid-cols-1'
          )}>
            {assets.map((asset) => (
              <div
                key={asset.id}
                onClick={() => openAssetDetail(asset)}
                className={clsx(
                  'card-elevated cursor-pointer group overflow-hidden',
                  viewMode === 'list' && 'flex items-center'
                )}
              >
                <div className={clsx(
                  'bg-bg-elevated relative overflow-hidden',
                  viewMode === 'grid' ? 'aspect-[3/4]' : 'w-20 h-20 flex-shrink-0'
                )}>
                  {getImageUrl(asset) ? (
                    <img
                      src={getImageUrl(asset)!}
                      alt={asset.name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-text-tertiary">
                      <Image className="w-8 h-8" />
                    </div>
                  )}
                  {asset.isGenerated && (
                    <div className="absolute top-2 left-2 bg-accent-gold/90 text-white px-2 py-0.5 rounded text-xs font-medium z-10">
                      AI 생성
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2">
                    <div className="absolute top-2 right-2 flex gap-1">
                      <button
                        onClick={(e) => toggleFavorite(asset, e)}
                        className="p-1.5 bg-black/50 hover:bg-black/70 rounded-full transition-colors"
                        title={asset.isFavorited ? '즐겨찾기 해제' : '즐겨찾기'}
                      >
                        <Star className={clsx('w-4 h-4', asset.isFavorited ? 'fill-accent-gold text-accent-gold' : 'text-white')} />
                      </button>
                      {viewFilter === 'trash' ? (
                        <button
                          onClick={(e) => restoreAsset(asset, e)}
                          className="p-1.5 bg-black/50 hover:bg-black/70 rounded-full transition-colors"
                          disabled={processingAction}
                          title="복구"
                        >
                          <Upload className="w-4 h-4 text-white" />
                        </button>
                      ) : viewFilter === 'hidden' ? (
                        <button
                          onClick={(e) => unhideAsset(asset, e)}
                          className="p-1.5 bg-black/50 hover:bg-black/70 rounded-full transition-colors"
                          disabled={processingAction}
                          title="숨김 해제"
                        >
                          <Eye className="w-4 h-4 text-white" />
                        </button>
                      ) : asset.isOwner ? (
                        <button
                          onClick={(e) => openDeleteModal(asset, e)}
                          className="p-1.5 bg-black/50 hover:bg-red-500/70 rounded-full transition-colors"
                          title="삭제"
                        >
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </button>
                      ) : (
                        <button
                          onClick={(e) => hideAsset(asset, e)}
                          className="p-1.5 bg-black/50 hover:bg-black/70 rounded-full transition-colors"
                          disabled={processingAction}
                          title="숨기기"
                        >
                          <EyeOff className="w-4 h-4 text-white" />
                        </button>
                      )}
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleUseAsset(asset)
                      }}
                      className="btn btn-primary text-sm"
                    >
                      <ScanSearch className="w-3 h-3 mr-1" />
                      분석하기
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        openPaletteModal(asset)
                      }}
                      className="btn btn-secondary text-sm"
                    >
                      <FolderPlus className="w-3 h-3 mr-1" />
                      팔레트 추가
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        findSimilarImages(asset)
                      }}
                      className="btn btn-secondary text-sm"
                    >
                      <Search className="w-3 h-3 mr-1" />
                      유사 이미지
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        setInitialReferenceAsset(asset)
                        setShowGenerateModal(true)
                      }}
                      className="btn btn-primary text-sm"
                    >
                      <Sparkles className="w-3 h-3 mr-1" />
                      AI추출
                    </button>
                  </div>
                </div>
                <div className={clsx('p-3', viewMode === 'list' && 'flex-1 flex items-center justify-between')}>
                  <div className="flex-1">
                    <h3 className="font-medium truncate" title={asset.name}>{asset.name}</h3>
                    <p className="text-sm text-text-tertiary capitalize">{asset.type}</p>
                    {asset.userTags && asset.userTags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {asset.userTags.slice(0, 3).map((tag) => (
                          <span
                            key={tag}
                            className="inline-flex items-center gap-1 px-2 py-0.5 bg-accent-gold/10 text-accent-gold rounded text-xs"
                          >
                            <Tag className="w-2.5 h-2.5" />
                            {tag}
                          </span>
                        ))}
                        {asset.userTags.length > 3 && (
                          <span className="text-xs text-text-tertiary">+{asset.userTags.length - 3}</span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className={clsx(viewMode === 'grid' ? 'mt-1' : '')}>
                    <span className="text-xs text-text-tertiary">
                      {asset.viewCount || 0} views
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center mt-8">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-4 py-2 text-text-secondary hover:text-text-primary transition-colors disabled:opacity-50"
              >
                이전
              </button>

              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum: number
                if (totalPages <= 5) {
                  pageNum = i + 1
                } else if (page <= 3) {
                  pageNum = i + 1
                } else if (page >= totalPages - 2) {
                  pageNum = totalPages - 4 + i
                } else {
                  pageNum = page - 2 + i
                }
                return pageNum
              }).map((pageNum) => (
                <button
                  key={pageNum}
                  onClick={() => setPage(pageNum)}
                  className={clsx(
                    'w-10 h-10 rounded-lg transition-colors',
                    page === pageNum
                      ? 'bg-accent-gold text-white'
                      : 'text-text-secondary hover:bg-bg-elevated hover:text-text-primary'
                  )}
                >
                  {pageNum}
                </button>
              ))}

              {totalPages > 5 && page < totalPages - 2 && (
                <>
                  <span className="text-text-tertiary">...</span>
                  <button
                    onClick={() => setPage(totalPages)}
                    className="w-10 h-10 rounded-lg text-text-secondary hover:bg-bg-elevated hover:text-text-primary transition-colors"
                  >
                    {totalPages}
                  </button>
                </>
              )}

              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-4 py-2 text-text-secondary hover:text-text-primary transition-colors disabled:opacity-50"
              >
                다음
              </button>
            </div>
          </div>
        )}
      </main>

      {/* Palette Selection Modal */}
      {showPaletteModal && selectedAssetForPalette && (
        <div className="modal-overlay" onClick={closePaletteModal}>
          <div className="modal-content max-w-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="text-xl font-bold">프로젝트 팔레트에 추가</h2>
              <button onClick={closePaletteModal} className="text-text-tertiary hover:text-text-primary">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="modal-body">
              <p className="text-sm text-text-secondary mb-4">
                "{selectedAssetForPalette.name}"을(를) 추가할 프로젝트를 선택하세요.
              </p>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {projects.length === 0 ? (
                  <p className="text-center py-8 text-text-tertiary">프로젝트가 없습니다.</p>
                ) : (
                  projects.map((project) => (
                    <button
                      key={project.id}
                      onClick={() => addToProjectPalette(project.id, project.title)}
                      disabled={addingToPalette}
                      className="w-full text-left p-3 rounded-lg border border-border-default hover:bg-bg-elevated transition-colors disabled:opacity-50"
                    >
                      <div className="font-medium">{project.title}</div>
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Similar Images Modal */}
      {showSimilarModal && selectedAsset && (
        <div className="modal-overlay" onClick={closeSimilarModal}>
          <div className="modal-content max-w-[95vw]" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="text-xl font-bold">유사 이미지</h2>
              <button onClick={closeSimilarModal} className="text-text-tertiary hover:text-text-primary">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="modal-body">
              <div className="flex items-start gap-6 mb-6 pb-6 border-b border-border-subtle">
                {/* Selected Asset Image */}
                <div className="w-32 h-40 bg-bg-elevated rounded-lg overflow-hidden flex-shrink-0">
                  {getImageUrl(selectedAsset) ? (
                    <img src={getImageUrl(selectedAsset)!} alt={selectedAsset.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-text-tertiary">
                      <Image className="w-8 h-8" />
                    </div>
                  )}
                </div>

                {/* Asset Info and Filter Controls */}
                <div className="flex-1 space-y-4">
                  {/* Asset Info */}
                  <div>
                    <h3 className="font-medium text-lg mb-1">{selectedAsset.name}</h3>
                    <p className="text-sm text-text-tertiary capitalize">{selectedAsset.type}</p>
                    {selectedAsset.description && (
                      <p className="text-sm text-text-secondary mt-2">{selectedAsset.description}</p>
                    )}
                    {sourceCaption && (
                      <div className="mt-2 p-2 bg-bg-tertiary rounded text-xs text-text-secondary">
                        <span className="font-medium text-text-primary">AI 이미지 설명:</span> {sourceCaption}
                      </div>
                    )}
                  </div>

                  {/* Filter Controls */}
                  <div className="p-4 bg-bg-elevated rounded-lg border border-border-subtle space-y-3">
                <div className="flex flex-wrap items-center gap-3">
                  {/* Similarity Threshold */}
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-text-secondary whitespace-nowrap min-w-[80px]">유사도</label>
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={similarityThreshold}
                        onChange={(e) => {
                          const val = Number(e.target.value)
                          if (val >= 0 && val <= 100) {
                            setSimilarityThreshold(val)
                          }
                        }}
                        className="input py-1 px-2 text-sm w-[70px]"
                      />
                      <span className="text-sm text-text-secondary">% 이상</span>
                    </div>
                  </div>

                  {/* Result Limit */}
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-text-secondary whitespace-nowrap min-w-[80px]">검색 갯수</label>
                    <select
                      value={similarResultLimit}
                      onChange={(e) => setSimilarResultLimit(Number(e.target.value))}
                      className="input py-1 px-2 text-sm w-[90px]"
                    >
                      <option value={20}>20개</option>
                      <option value={40}>40개</option>
                      <option value={60}>60개</option>
                      <option value={80}>80개</option>
                      <option value={99}>99개</option>
                    </select>
                  </div>

                  {/* Tag Filter */}
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-text-secondary whitespace-nowrap min-w-[80px]">태그 필터</label>
                    <select
                      value={tagFilter}
                      onChange={(e) => setTagFilter(e.target.value as 'all' | 'has' | 'missing')}
                      className="input py-1 px-2 text-sm w-[110px]"
                    >
                      <option value="all">전체</option>
                      <option value="has">태그 있음</option>
                      <option value="missing">태그 없음</option>
                    </select>
                    {tagFilter !== 'all' && (
                      <input
                        type="text"
                        value={filterTagName}
                        onChange={(e) => setFilterTagName(e.target.value)}
                        placeholder="태그명"
                        className="input py-1 px-2 text-sm w-32"
                      />
                    )}
                  </div>

                  {/* Search Mode */}
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-text-secondary whitespace-nowrap min-w-[80px]">검색 모드</label>
                    <select
                      value={searchMode}
                      onChange={(e) => setSearchMode(e.target.value as 'hybrid' | 'visual' | 'semantic')}
                      className="input py-1 px-2 text-sm w-[110px]"
                    >
                      <option value="hybrid">하이브리드</option>
                      <option value="visual">시각</option>
                      <option value="semantic">의미</option>
                    </select>
                  </div>

                  {/* Search Button */}
                  <button
                    onClick={async () => {
                      if (selectedAsset) {
                        setSimilarLoading(true)
                        setSourceCaption(null)
                        try {
                          const result = await searchApi.similar(selectedAsset.id, similarResultLimit, searchMode)
                          if (result.success && result.data) {
                            setSimilarAssets(result.data)
                            setSourceCaption((result as any).meta?.sourceCaption || null)
                          }
                        } catch (err) {
                          console.error('Similar search failed:', err)
                        } finally {
                          setSimilarLoading(false)
                        }
                      }
                    }}
                    disabled={similarLoading}
                    className="btn btn-primary btn-sm disabled:opacity-50"
                  >
                    {similarLoading ? '검색 중...' : '검색'}
                  </button>

                  {/* Edit Mode Toggle */}
                  <div className="ml-auto flex items-center gap-2">
                    <button
                      onClick={() => {
                        setEditMode(!editMode)
                        if (editMode) {
                          setSelectedSimilarIds(new Set())
                        }
                      }}
                      className={clsx(
                        'btn btn-sm',
                        editMode ? 'btn-primary' : 'btn-secondary'
                      )}
                    >
                      <Edit2 className="w-4 h-4 mr-1" />
                      {editMode ? '편집 모드' : '편집'}
                    </button>
                  </div>
                </div>

                {/* Batch Edit Controls */}
                {editMode && (
                  <div className="pt-4 border-t border-border-subtle space-y-3">
                    <div className="flex items-center gap-2">
                      <button onClick={selectAll} className="btn btn-sm btn-secondary">
                        전체 선택
                      </button>
                      <button onClick={deselectAll} className="btn btn-sm btn-secondary">
                        선택 해제
                      </button>
                      <span className="text-sm text-text-secondary ml-2">
                        {selectedSimilarIds.size}개 선택됨
                      </span>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                      <select
                        value={batchOperation}
                        onChange={(e) => setBatchOperation(e.target.value as 'add' | 'remove')}
                        className="input py-1 px-2 text-sm"
                      >
                        <option value="add">태그 추가</option>
                        <option value="remove">태그 삭제</option>
                      </select>
                      <input
                        type="text"
                        value={batchTagName}
                        onChange={(e) => setBatchTagName(e.target.value)}
                        placeholder="태그명"
                        className="input py-1 px-2 text-sm flex-1 min-w-[150px]"
                      />
                      <button
                        onClick={applyBatchTags}
                        disabled={selectedSimilarIds.size === 0 || !batchTagName.trim() || applyingBatchTags}
                        className="btn btn-primary btn-sm disabled:opacity-50"
                      >
                        {applyingBatchTags ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                            처리 중...
                          </>
                        ) : (
                          <>
                            <Check className="w-4 h-4 mr-1" />
                            적용
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}
                  </div>
                </div>
              </div>

              {similarLoading ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 className="w-8 h-8 animate-spin text-accent-gold" />
                  <span className="ml-3 text-text-secondary">유사 이미지를 검색하는 중...</span>
                </div>
              ) : similarAssets.length === 0 ? (
                <div className="text-center py-20">
                  <Image className="w-16 h-16 mx-auto text-text-tertiary mb-4" />
                  <p className="text-text-secondary">유사한 이미지를 찾지 못했습니다.</p>
                </div>
              ) : (() => {
                const filteredAssets = getFilteredSimilarAssets()
                return (
                  <div>
                    <p className="text-sm text-text-secondary mb-4">
                      {filteredAssets.length}개의 유사 이미지를 찾았습니다.
                      {filteredAssets.length !== similarAssets.length && (
                        <span className="text-text-tertiary ml-1">(전체 {similarAssets.length}개 중)</span>
                      )}
                    </p>
                    {filteredAssets.length === 0 ? (
                      <div className="text-center py-10">
                        <Filter className="w-12 h-12 mx-auto text-text-tertiary mb-3" />
                        <p className="text-text-secondary">필터 조건에 맞는 이미지가 없습니다.</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                        {filteredAssets.map((similar) => {
                          const isSelected = selectedSimilarIds.has(similar.id)
                          return (
                            <div
                              key={similar.id}
                              className={clsx(
                                'card-elevated cursor-pointer group relative',
                                editMode && 'hover:ring-2 hover:ring-accent-gold',
                                isSelected && 'ring-2 ring-accent-gold'
                              )}
                              onClick={() => {
                                if (editMode) {
                                  toggleSelection(similar.id)
                                } else {
                                  closeSimilarModal()
                                  openAssetDetail(similar)
                                }
                              }}
                            >
                              <div className="bg-bg-elevated aspect-[3/4] relative overflow-hidden rounded-lg">
                                {getImageUrl(similar) ? (
                                  <img
                                    src={getImageUrl(similar)!}
                                    alt={similar.name}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <div className="absolute inset-0 flex items-center justify-center text-text-tertiary">
                                    <Image className="w-8 h-8" />
                                  </div>
                                )}
                                <div className="absolute top-2 right-2 flex flex-col items-end gap-1">
                                  <div className="bg-black/75 px-2 py-1 rounded text-xs font-medium">
                                    {(similar.similarity * 100).toFixed(0)}%
                                  </div>
                                  {searchMode === 'hybrid' && similar.visualScore !== undefined && similar.semanticScore !== undefined && (
                                    <div className="bg-black/60 px-1.5 py-0.5 rounded text-[10px] text-text-tertiary">
                                      V{(similar.visualScore * 100).toFixed(0)} S{(similar.semanticScore * 100).toFixed(0)}
                                    </div>
                                  )}
                                </div>
                                {editMode && (
                                  <div className="absolute top-2 left-2">
                                    <div className={clsx(
                                      'w-6 h-6 rounded border-2 flex items-center justify-center',
                                      isSelected ? 'bg-accent-gold border-accent-gold' : 'bg-black/50 border-white'
                                    )}>
                                      {isSelected && <Check className="w-4 h-4 text-black" />}
                                    </div>
                                  </div>
                                )}
                              </div>
                              <div className="p-2">
                                <h4 className="text-sm font-medium truncate" title={similar.image_caption || similar.name}>{similar.name}</h4>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>
                )
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedAssetForDelete && (
        <div className="modal-overlay" onClick={closeDeleteModal}>
          <div className="modal-content max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <AlertTriangle className="w-6 h-6 text-red-500" />
                Asset 삭제
              </h2>
              <button onClick={closeDeleteModal} className="text-text-tertiary hover:text-text-primary">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="modal-body">
              <p className="text-text-secondary mb-4">
                "{selectedAssetForDelete.name}"을(를) 삭제하시겠습니까?
              </p>
              <p className="text-sm text-text-tertiary">
                삭제된 Asset은 30일 이내에 휴지통에서 복구할 수 있습니다.
              </p>
            </div>
            <div className="modal-footer">
              <button onClick={closeDeleteModal} className="btn btn-secondary">
                취소
              </button>
              <button
                onClick={confirmDelete}
                disabled={processingAction}
                className="btn bg-red-500 hover:bg-red-600 text-white disabled:opacity-50"
              >
                {processingAction ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    삭제 중...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    삭제
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Asset Detail Modal */}
      {showAssetDetailModal && selectedAssetForDetail && (
        <div className="modal-overlay" onClick={closeAssetDetail}>
          <div className="modal-content max-w-5xl" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="flex-1 flex items-center gap-2 min-w-0">
                {editingTitle ? (
                  <>
                    <input
                      type="text"
                      value={editedTitle}
                      onChange={(e) => setEditedTitle(e.target.value)}
                      className="input flex-1 text-lg font-bold"
                      placeholder="제목 입력"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleSaveTitle()
                        } else if (e.key === 'Escape') {
                          handleCancelEditTitle()
                        }
                      }}
                    />
                    <button
                      onClick={handleSaveTitle}
                      disabled={savingTitle}
                      className="p-2 hover:bg-bg-elevated rounded-lg transition-colors text-green-500 hover:text-green-400 disabled:opacity-50"
                      title="저장 (Enter)"
                    >
                      {savingTitle ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <Check className="w-5 h-5" />
                      )}
                    </button>
                    <button
                      onClick={handleCancelEditTitle}
                      disabled={savingTitle}
                      className="p-2 hover:bg-bg-elevated rounded-lg transition-colors text-text-tertiary hover:text-text-primary disabled:opacity-50"
                      title="취소 (Esc)"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </>
                ) : (
                  <>
                    <h2 className="text-xl font-bold truncate flex-1" title={selectedAssetForDetail.name}>
                      {selectedAssetForDetail.name}
                    </h2>
                    <button
                      onClick={() => setEditingTitle(true)}
                      className="p-2 hover:bg-bg-elevated rounded-lg transition-colors text-text-tertiary hover:text-accent-gold"
                      title="제목 수정"
                    >
                      <Edit2 className="w-5 h-5" />
                    </button>
                  </>
                )}
              </div>
              <button onClick={closeAssetDetail} className="text-text-tertiary hover:text-text-primary ml-2">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="modal-body">
              {/* Tabs */}
              <div className="border-b border-border-subtle mb-6">
                <div className="flex gap-4">
                  <button
                    onClick={() => setActiveTab('info')}
                    className={clsx(
                      'px-4 py-2 border-b-2 transition-colors',
                      activeTab === 'info'
                        ? 'border-accent-gold text-accent-gold'
                        : 'border-transparent text-text-secondary hover:text-text-primary'
                    )}
                  >
                    <Info className="w-4 h-4 inline mr-2" />
                    정보
                  </button>
                  <button
                    onClick={() => setActiveTab('generation')}
                    className={clsx(
                      'px-4 py-2 border-b-2 transition-colors',
                      activeTab === 'generation'
                        ? 'border-accent-gold text-accent-gold'
                        : 'border-transparent text-text-secondary hover:text-text-primary'
                    )}
                  >
                    <Sparkles className="w-4 h-4 inline mr-2" />
                    생성 정보
                  </button>
                  <button
                    onClick={() => setActiveTab('analysis')}
                    className={clsx(
                      'px-4 py-2 border-b-2 transition-colors',
                      activeTab === 'analysis'
                        ? 'border-accent-gold text-accent-gold'
                        : 'border-transparent text-text-secondary hover:text-text-primary'
                    )}
                  >
                    <FileText className="w-4 h-4 inline mr-2" />
                    분석 정보
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Image Preview */}
                <div>
                  <div className="bg-bg-elevated rounded-lg overflow-hidden aspect-[3/4]">
                    {getImageUrl(selectedAssetForDetail) ? (
                      <img
                        src={getImageUrl(selectedAssetForDetail)!}
                        alt={selectedAssetForDetail.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-text-tertiary">
                        <Image className="w-16 h-16" />
                      </div>
                    )}
                  </div>
                </div>

                {/* Details */}
                <div className="space-y-4">
                  {activeTab === 'info' && (
                    <>
                      <div>
                        <label className="text-sm font-medium text-text-tertiary block mb-1">ID</label>
                        <p className="text-text-secondary font-mono text-sm">{selectedAssetForDetail.id}</p>
                      </div>

                      <div>
                        <label className="text-sm font-medium text-text-tertiary block mb-1">타입</label>
                        <p className="text-text-primary capitalize">{selectedAssetForDetail.type}</p>
                      </div>

                      {selectedAssetForDetail.description && (
                        <div>
                          <label className="text-sm font-medium text-text-tertiary block mb-1">설명</label>
                          <p className="text-text-secondary">{selectedAssetForDetail.description}</p>
                        </div>
                      )}

                      <div>
                        <label className="text-sm font-medium text-text-tertiary block mb-1">통계</label>
                        <div className="grid grid-cols-2 gap-2">
                          <div className="p-3 bg-bg-elevated rounded-lg">
                            <p className="text-xs text-text-tertiary">조회수</p>
                            <p className="text-lg font-semibold">{selectedAssetForDetail.viewCount || 0}</p>
                          </div>
                          <div className="p-3 bg-bg-elevated rounded-lg">
                            <p className="text-xs text-text-tertiary">다운로드</p>
                            <p className="text-lg font-semibold">{selectedAssetForDetail.downloadCount || 0}</p>
                          </div>
                        </div>
                      </div>

                      {/* Master Tags */}
                      {selectedAssetForDetail.tags && selectedAssetForDetail.tags.length > 0 && (
                        <div>
                          <label className="text-sm font-medium text-text-tertiary block mb-2">마스터 태그</label>
                          <div className="flex flex-wrap gap-2">
                            {selectedAssetForDetail.tags.map((tag) => (
                              <span
                                key={tag.id}
                                className="inline-flex items-center gap-1 px-2 py-1 bg-blue-500/10 text-blue-400 rounded text-sm"
                              >
                                <Tag className="w-3 h-3" />
                                {tag.name}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Keywords */}
                      {selectedAssetForDetail.keywords && selectedAssetForDetail.keywords.length > 0 && (
                        <div>
                          <label className="text-sm font-medium text-text-tertiary block mb-2">키워드</label>
                          <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                            {selectedAssetForDetail.keywords.map((kw, idx) => (
                              <span
                                key={idx}
                                className="inline-flex items-center gap-1 px-2 py-1 bg-purple-500/10 text-purple-400 rounded text-xs"
                                title={`타입: ${kw.keyword_type}, 요소: ${kw.element_type || 'N/A'}, 신뢰도: ${kw.confidence}`}
                              >
                                {kw.keyword}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* User Tags */}
                      <div>
                        <label className="text-sm font-medium text-text-tertiary block mb-2">내 태그</label>
                        <div className="space-y-2">
                          <div className="flex flex-wrap gap-2">
                            {selectedAssetForDetail.userTags && selectedAssetForDetail.userTags.length > 0 ? (
                              selectedAssetForDetail.userTags.map((tag) => (
                                <span
                                  key={tag}
                                  className="inline-flex items-center gap-1 px-2 py-1 bg-accent-gold/10 text-accent-gold rounded text-sm"
                                >
                                  <Tag className="w-3 h-3" />
                                  {tag}
                                  <button
                                    onClick={() => removeUserTag(tag)}
                                    className="hover:text-red-400 ml-1"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </span>
                              ))
                            ) : (
                              <p className="text-sm text-text-tertiary">태그가 없습니다.</p>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              placeholder="새 태그 추가..."
                              value={newTagInput}
                              onChange={(e) => setNewTagInput(e.target.value)}
                              onKeyDown={(e) => e.key === 'Enter' && addUserTag()}
                              className="input flex-1 text-sm"
                            />
                            <button
                              onClick={addUserTag}
                              disabled={!newTagInput.trim() || processingAction}
                              className="btn btn-primary text-sm disabled:opacity-50"
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>

                      {selectedAssetForDetail.createdAt && (
                        <div>
                          <label className="text-sm font-medium text-text-tertiary block mb-1">생성일</label>
                          <p className="text-text-secondary text-sm">
                            {new Date(selectedAssetForDetail.createdAt).toLocaleString('ko-KR')}
                          </p>
                        </div>
                      )}
                    </>
                  )}

                  {activeTab === 'generation' && (
                    <>
                      {selectedAssetForDetail.isGenerated ? (
                        <>
                          {selectedAssetForDetail.generationPrompt && (
                            <div>
                              <label className="text-sm font-medium text-text-tertiary block mb-1">생성 프롬프트</label>
                              <div className="p-3 bg-bg-elevated rounded-lg">
                                <p className="text-sm text-text-secondary whitespace-pre-wrap">
                                  {selectedAssetForDetail.generationPrompt}
                                </p>
                              </div>
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="text-center py-8 text-text-tertiary">
                          <Info className="w-12 h-12 mx-auto mb-3 opacity-50" />
                          <p>AI 생성 Asset이 아닙니다.</p>
                        </div>
                      )}
                    </>
                  )}

                  {activeTab === 'analysis' && (
                    <>
                      {loadingAnalysis ? (
                        <div className="flex items-center justify-center py-12">
                          <Loader2 className="w-8 h-8 animate-spin text-accent-gold" />
                          <span className="ml-3 text-text-secondary">분석 정보를 불러오는 중...</span>
                        </div>
                      ) : assetAnalysis ? (
                        <div className="space-y-4">
                          {assetAnalysis.backgroundAnalysis && (
                            <div>
                              <label className="text-sm font-medium text-text-tertiary flex items-center justify-between mb-1">
                                <span>배경 분석</span>
                                <button
                                  onClick={() => setShowDetailJson({ type: 'background', data: assetAnalysis.backgroundAnalysis })}
                                  className="text-xs text-accent-gold hover:text-accent-gold/80"
                                >
                                  JSON 보기
                                </button>
                              </label>
                              <div className="p-3 bg-bg-elevated rounded-lg max-h-32 overflow-y-auto">
                                <pre className="text-xs text-text-secondary whitespace-pre-wrap">
                                  {typeof assetAnalysis.backgroundAnalysis === 'string'
                                    ? assetAnalysis.backgroundAnalysis
                                    : JSON.stringify(assetAnalysis.backgroundAnalysis, null, 2)}
                                </pre>
                              </div>
                            </div>
                          )}

                          {assetAnalysis.typographyAnalysis && (
                            <div>
                              <label className="text-sm font-medium text-text-tertiary flex items-center justify-between mb-1">
                                <span>타이포그래피 분석</span>
                                <button
                                  onClick={() => setShowDetailJson({ type: 'typography', data: assetAnalysis.typographyAnalysis })}
                                  className="text-xs text-accent-gold hover:text-accent-gold/80"
                                >
                                  JSON 보기
                                </button>
                              </label>
                              <div className="p-3 bg-bg-elevated rounded-lg max-h-32 overflow-y-auto">
                                <pre className="text-xs text-text-secondary whitespace-pre-wrap">
                                  {typeof assetAnalysis.typographyAnalysis === 'string'
                                    ? assetAnalysis.typographyAnalysis
                                    : JSON.stringify(assetAnalysis.typographyAnalysis, null, 2)}
                                </pre>
                              </div>
                            </div>
                          )}

                          {assetAnalysis.characterAnalysis && (
                            <div>
                              <label className="text-sm font-medium text-text-tertiary flex items-center justify-between mb-1">
                                <span>캐릭터 분석</span>
                                <button
                                  onClick={() => setShowDetailJson({ type: 'character', data: assetAnalysis.characterAnalysis })}
                                  className="text-xs text-accent-gold hover:text-accent-gold/80"
                                >
                                  JSON 보기
                                </button>
                              </label>
                              <div className="p-3 bg-bg-elevated rounded-lg max-h-32 overflow-y-auto">
                                <pre className="text-xs text-text-secondary whitespace-pre-wrap">
                                  {typeof assetAnalysis.characterAnalysis === 'string'
                                    ? assetAnalysis.characterAnalysis
                                    : JSON.stringify(assetAnalysis.characterAnalysis, null, 2)}
                                </pre>
                              </div>
                            </div>
                          )}

                          {assetAnalysis.effectAnalysis && (
                            <div>
                              <label className="text-sm font-medium text-text-tertiary flex items-center justify-between mb-1">
                                <span>효과 분석</span>
                                <button
                                  onClick={() => setShowDetailJson({ type: 'effect', data: assetAnalysis.effectAnalysis })}
                                  className="text-xs text-accent-gold hover:text-accent-gold/80"
                                >
                                  JSON 보기
                                </button>
                              </label>
                              <div className="p-3 bg-bg-elevated rounded-lg max-h-32 overflow-y-auto">
                                <pre className="text-xs text-text-secondary whitespace-pre-wrap">
                                  {typeof assetAnalysis.effectAnalysis === 'string'
                                    ? assetAnalysis.effectAnalysis
                                    : JSON.stringify(assetAnalysis.effectAnalysis, null, 2)}
                                </pre>
                              </div>
                            </div>
                          )}

                          {assetAnalysis.created_at && (
                            <div>
                              <label className="text-sm font-medium text-text-tertiary block mb-1">분석 일시</label>
                              <p className="text-text-secondary text-sm">
                                {new Date(assetAnalysis.created_at).toLocaleString('ko-KR')}
                              </p>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-text-tertiary">
                          <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                          <p>분석 정보가 없습니다.</p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Modal Footer with Action Buttons */}
            <div className="modal-footer border-t border-border-subtle">
              <div className="flex items-center justify-between gap-3">
                {/* Left side actions */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      toggleFavorite(selectedAssetForDetail, e)
                    }}
                    className={clsx(
                      'btn btn-secondary',
                      selectedAssetForDetail.isFavorited && 'bg-accent-gold/10 text-accent-gold hover:bg-accent-gold/20'
                    )}
                    disabled={processingAction}
                  >
                    <Star className={clsx('w-4 h-4 mr-2', selectedAssetForDetail.isFavorited && 'fill-accent-gold')} />
                    {selectedAssetForDetail.isFavorited ? '즐겨찾기 해제' : '즐겨찾기'}
                  </button>

                  {viewFilter === 'trash' ? (
                    <button
                      onClick={(e) => {
                        restoreAsset(selectedAssetForDetail, e)
                        closeAssetDetail()
                      }}
                      className="btn btn-secondary"
                      disabled={processingAction}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      복구
                    </button>
                  ) : viewFilter === 'hidden' ? (
                    <button
                      onClick={(e) => {
                        unhideAsset(selectedAssetForDetail, e)
                        closeAssetDetail()
                      }}
                      className="btn btn-secondary"
                      disabled={processingAction}
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      숨김 해제
                    </button>
                  ) : (
                    <>
                      {selectedAssetForDetail.isOwner ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            closeAssetDetail()
                            openDeleteModal(selectedAssetForDetail, e)
                          }}
                          className="btn bg-red-500/10 text-red-400 hover:bg-red-500/20"
                          disabled={processingAction}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          삭제
                        </button>
                      ) : (
                        <button
                          onClick={(e) => {
                            hideAsset(selectedAssetForDetail, e)
                            closeAssetDetail()
                          }}
                          className="btn btn-secondary"
                          disabled={processingAction}
                        >
                          <EyeOff className="w-4 h-4 mr-2" />
                          숨기기
                        </button>
                      )}
                    </>
                  )}
                </div>

                {/* Right side actions */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      closeAssetDetail()
                      findSimilarImages(selectedAssetForDetail)
                    }}
                    className="btn btn-secondary"
                  >
                    <Search className="w-4 h-4 mr-2" />
                    유사 이미지
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      closeAssetDetail()
                      openPaletteModal(selectedAssetForDetail)
                    }}
                    className="btn btn-secondary"
                  >
                    <FolderPlus className="w-4 h-4 mr-2" />
                    팔레트 추가
                  </button>
                  <button
                    onClick={() => {
                      closeAssetDetail()
                      handleUseAsset(selectedAssetForDetail)
                    }}
                    className="btn btn-primary"
                  >
                    <ScanSearch className="w-4 h-4 mr-2" />
                    이미지 분석
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Detail JSON Viewer Modal */}
      {showDetailJson && (
        <div className="modal-overlay" onClick={() => setShowDetailJson(null)}>
          <div className="modal-content max-w-3xl" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="text-xl font-bold">{showDetailJson.type} - JSON</h2>
              <button onClick={() => setShowDetailJson(null)} className="text-text-tertiary hover:text-text-primary">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="modal-body">
              <div className="bg-bg-elevated rounded-lg p-4 max-h-[60vh] overflow-auto">
                <pre className="text-sm text-text-secondary">
                  {JSON.stringify(showDetailJson.data, null, 2)}
                </pre>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <UploadAssetModal
          onClose={() => setShowUploadModal(false)}
          onSuccess={() => {
            setShowUploadModal(false)
            setReloadTrigger(prev => prev + 1)
          }}
        />
      )}

      {/* Generate Modal */}
      {showGenerateModal && (
        <GenerateAssetModal
          onClose={() => {
            setShowGenerateModal(false)
            setInitialReferenceAsset(null)
          }}
          onSuccess={() => {
            setShowGenerateModal(false)
            setInitialReferenceAsset(null)
            setReloadTrigger(prev => prev + 1)
          }}
          initialReferenceAsset={initialReferenceAsset}
        />
      )}

      {/* Generate History Modal */}
      {showHistoryModal && (
        <GenerateHistoryModal
          onClose={() => setShowHistoryModal(false)}
        />
      )}
    </div>
  )
}
